import { useState, useEffect } from 'preact/hooks';
import { ledger } from '../lib/ledger';
import { InfoTip } from './InfoTip';
import { dataHub, type LiveExecStatus } from '../lib/datahub';
import {
  getSettings,
  setSettings,
  clampSettings,
  CAESAR_CAPS,
  caesarTradeCount,
  type CaesarAutoTradeSettings,
} from '../lib/caesar-autotrade';

// Owner-only Settings overlay (opened from the lock pill, like AdminPanel). Two sections:
// (1) configure Caesar's auto-PAPER-trade ($100k paper account, risk %, max open positions),
// clamped to the Caesar caps shown read-only; (2) reset Caesar's trade history.
export function SettingsPanel({ onClose }: { onClose: () => void }) {
  const [s, setS] = useState<CaesarAutoTradeSettings>(getSettings());
  const [capitalDraft, setCapitalDraft] = useState(String(getSettings().startingCapital));
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [count, setCount] = useState(caesarTradeCount());
  const [resetMsg, setResetMsg] = useState<string | null>(null);

  const update = (patch: Partial<CaesarAutoTradeSettings>) => {
    const next = clampSettings({ ...s, ...patch });
    setS(next);
    setSettings(next); // persist immediately, already clamped to the Caesar caps
    setSavedMsg('Saved.');
  };

  const resetCaesar = () => {
    if (!confirm("Reset ALL of Caesar's auto-paper trade history? Your own (Operator) trades are NOT affected. This can't be undone.")) return;
    ledger.clearCaesarTrades();
    setCount(caesarTradeCount());
    setResetMsg("Caesar's paper trade history was cleared.");
  };

  // ── Owner Settings master lock for live execution ──
  const localHub = dataHub.isLocal();
  const [live, setLive] = useState<LiveExecStatus | null>(null);
  const [pass, setPass] = useState('');
  const [otp, setOtp] = useState('');
  const [lockBusy, setLockBusy] = useState(false);
  const [lockMsg, setLockMsg] = useState<{ tone: string; text: string } | null>(null);
  const refreshLive = () => {
    if (!localHub) { setLive({ ok: false }); return; }
    dataHub.liveExecStatus().then(setLive).catch(() => setLive({ ok: false }));
  };
  useEffect(() => { refreshLive(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  // "Missing Broker" whenever the hub can't actually execute live (off-hub, gated,
  // keys/endpoint/config incomplete). configured===true means keys + passphrase +
  // 2FA + caps + enabled are ALL present, i.e. live execution is genuinely possible.
  const liveReady = live?.ok === true && live?.configured === true;
  const locked = live?.locked !== false; // default/undefined -> locked
  const doUnlock = () => {
    if (lockBusy || pass.length < 1 || otp.length < 6) return;
    setLockBusy(true); setLockMsg(null);
    dataHub.liveUiUnlock({ passphrase: pass, code: otp })
      .then((st) => {
        if (st.ok && st.locked === false) { setLive(st); setPass(''); setOtp(''); setLockMsg({ tone: 'om-good', text: 'Live execution unlocked. It stays owner-gated (arm still needs passphrase + per-order 2FA).' }); }
        else { setLockMsg({ tone: 'om-bad', text: st.error || 'Unlock refused.' }); refreshLive(); }
      })
      .catch((e) => { setLockMsg({ tone: 'om-bad', text: (e as Error).message }); })
      .finally(() => setLockBusy(false));
  };
  const doLock = () => {
    setLockBusy(true); setLockMsg(null);
    dataHub.liveUiLock()
      .then((st) => { setLive(st); setLockMsg({ tone: 'om-good', text: 'Live execution locked.' }); })
      .catch((e) => { setLockMsg({ tone: 'om-bad', text: (e as Error).message }); })
      .finally(() => setLockBusy(false));
  };

  return (
    <div
      class="om-auth-gate"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div class="om-auth-card om-admin-card">
        <p class="om-auth-kicker">Settings</p>
        <h2>Caesar Auto-Paper-Trade</h2>
        <p>
          Let Caesar automatically log the setups he'd take into his own <strong>paper</strong> account. Paper automation stays separate from
          Owner-Only live execution, and every entry is capped by the Caesar lock.
        </p>

        <label class="om-auth-row">
          <input type="checkbox" checked={s.enabled} onChange={(e) => update({ enabled: (e.target as HTMLInputElement).checked })} /> Enable Caesar
          auto-paper-trade
        </label>

        <div class="om-input-grid cols-2" style="margin-top:10px">
          <div class="om-field">
            <label>
              Starting Capital ($)
              <InfoTip text="Caesar's own paper starting balance — the account he sizes his trades against." label="Starting Capital" />
            </label>
            <input
              type="number"
              value={capitalDraft}
              onInput={(e) => setCapitalDraft((e.target as HTMLInputElement).value)}
              onBlur={() => {
                const c = clampSettings({ ...s, startingCapital: Number(capitalDraft) || s.startingCapital });
                update({ startingCapital: c.startingCapital });
                setCapitalDraft(String(c.startingCapital));
              }}
            />
          </div>
          <div class="om-field">
            <label>
              Risk per Trade — {Math.round(s.riskPct * 10) / 10}%
              <InfoTip text="How much of his account Caesar risks on each trade. Drag to set anywhere from 0.1% to 5%." label="Risk per Trade" />
            </label>
            <input
              type="range"
              class="om-range"
              min="0.1"
              max="5"
              step="0.1"
              value={s.riskPct}
              onInput={(e) => update({ riskPct: Number((e.target as HTMLInputElement).value) })}
            />
          </div>
          <div class="om-field">
            <label>
              Max Open Positions — {s.maxOpenPositions}
              <InfoTip text={`How many positions Caesar can hold at once. Capped at ${CAESAR_CAPS.maxOpenPositions} by the Caesar lock.`} label="Max Open Positions" />
            </label>
            <input
              type="range"
              class="om-range"
              min="1"
              max={CAESAR_CAPS.maxOpenPositions}
              step="1"
              value={s.maxOpenPositions}
              onInput={(e) => update({ maxOpenPositions: Number((e.target as HTMLInputElement).value) })}
            />
          </div>
        </div>
        <label class="om-auth-row">
          <input type="checkbox" checked={s.strongOnly} onChange={(e) => update({ strongOnly: (e.target as HTMLInputElement).checked })} /> Only take the
          strongest setups (skip "marginal" / "fair")
        </label>
        <label class="om-auth-row">
          <input type="checkbox" checked={s.mirrorToAlpaca} onChange={(e) => update({ mirrorToAlpaca: (e.target as HTMLInputElement).checked })} /> Also mirror
          stock day-trade setups to my real Alpaca <strong>paper</strong> account (desktop only)
        </label>
        <p class="om-auth-hint" style="margin:2px 0 0">
          When on, each <strong>stock day-trade</strong> setup Caesar logs is also <em>attempted</em> on your Alpaca <strong>paper</strong> account through the
          local DataHub — check the <strong>Broker</strong> panel for what actually landed. Paper only: live orders require the separate Owner-Only arm flow. Options /
          forex / commodities are simulated locally only, and any single share over the ${CAESAR_CAPS.maxTradeDollars} per-trade cap is skipped.
        </p>
        {savedMsg && (
          <p class="om-good" style="margin:8px 0 0">
            {savedMsg}
          </p>
        )}

        <div class="om-admin-form" style="margin-top:14px">
          <p class="om-auth-kicker" style="margin:2px 0 0">
            Caesar guardrails (read-only)
          </p>
          <p class="om-auth-hint">
            Paper mode · max {CAESAR_CAPS.maxOpenPositions} open · max {CAESAR_CAPS.maxTradesPerDay} trades/day · max ${CAESAR_CAPS.maxTradeDollars}/trade ·
            max ${CAESAR_CAPS.maxDailyLoss} daily loss. Caesar's settings stay inside these caps; every auto-entry is reviewed by the Owner-Only
            Caesar lock before any live path could be considered.
          </p>
        </div>

        <h2 style="margin-top:18px">Live Execution Lock <InfoTip text="A master switch that gates every Owner-Only live-execution function. Default OFF (locked). Unlocking needs your live passphrase + a fresh 2FA code, checked on the hub. It never bypasses the arm flow — arming an order still needs its own passphrase + per-order 2FA + caps." /></h2>
        {!localHub ? (
          <p class="om-muted" style="margin:0">Live execution runs on the desktop app (local DataHub) only — this switch appears there.</p>
        ) : !liveReady ? (
          <div class="om-frame" style="padding:12px;border-radius:10px">
            <div style="display:flex;align-items:center;gap:8px;justify-content:space-between;flex-wrap:wrap">
              <strong>Owner-Only Live Execution</strong>
              <span class="om-broker-badge om-broker-badge-ro">Missing Broker</span>
            </div>
            <p class="om-muted" style="margin:8px 0 0;font-size:12px">
              Locked and unavailable until live execution is fully set up: link live broker keys in <strong>Broker Accounts</strong>, set the live passphrase + 2FA, enable it, and set caps. Until your API keys + endpoint allow live execution, this stays <strong>Missing Broker</strong>.
            </p>
          </div>
        ) : (
          <div class="om-frame" style="padding:12px;border-radius:10px">
            <div style="display:flex;align-items:center;gap:8px;justify-content:space-between;flex-wrap:wrap">
              <strong>Owner-Only Live Execution</strong>
              <span class={`om-broker-badge ${locked ? 'om-broker-badge-ro' : 'om-broker-badge-armed'}`}>{locked ? 'Locked (Off)' : 'Unlocked (On)'}</span>
            </div>
            {locked ? (
              <>
                <p class="om-muted" style="margin:8px 0 10px;font-size:12px">
                  The switch is <strong>Off</strong>. Turning it on requires your live <strong>passphrase</strong> + a fresh <strong>2FA</strong> code (verified on the hub). This is an extra layer in front of arming — it does not replace the per-order 2FA.
                </p>
                <div class="om-input-grid cols-2">
                  <div class="om-field"><label>Live passphrase</label><input type="password" autocomplete="off" value={pass} onInput={(e) => setPass((e.target as HTMLInputElement).value)} /></div>
                  <div class="om-field"><label>2FA code</label><input inputMode="numeric" autocomplete="one-time-code" maxLength={6} value={otp} onInput={(e) => setOtp((e.target as HTMLInputElement).value.replace(/\D/g, ''))} /></div>
                </div>
                <div class="om-admin-form-actions" style="margin-top:10px">
                  <button class="om-auth-primary" type="button" disabled={lockBusy || otp.length < 6 || pass.length < 1} onClick={doUnlock}>{lockBusy ? 'Unlocking…' : 'Turn On (unlock)'}</button>
                </div>
              </>
            ) : (
              <>
                <p class="om-warn" style="margin:8px 0 10px;font-size:12px">
                  The switch is <strong>On</strong>. The Owner-Only arm flow is available on the live panels (it still requires passphrase + per-order 2FA + caps). Turn it off when you're done.
                </p>
                <div class="om-admin-form-actions">
                  <button class="om-auth-secondary" type="button" disabled={lockBusy} onClick={doLock}>{lockBusy ? 'Locking…' : 'Turn Off (lock)'}</button>
                </div>
              </>
            )}
            {lockMsg && <p class={lockMsg.tone} style="margin:8px 0 0;font-size:12px" role="status">{lockMsg.text}</p>}
          </div>
        )}

        <h2 style="margin-top:18px">Reset Trade History</h2>
        <p>
          Caesar currently has <strong>{count}</strong> paper trade{count === 1 ? '' : 's'} on record. Clearing wipes only HIS auto-paper trades — your own
          (Operator) trades stay.
        </p>
        {resetMsg && (
          <p class="om-good" style="margin:0 0 8px">
            {resetMsg}
          </p>
        )}
        <div class="om-admin-form-actions">
          <button class="om-auth-secondary" type="button" onClick={resetCaesar} disabled={count === 0}>
            Reset Caesar trade history
          </button>
        </div>

        <div class="om-auth-actions">
          <button class="om-auth-primary" type="button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
